import { Ioption } from '../../models';

/*
 * Pure helpers for MultiSelectFilterDropdown / MultiSelectFilterList.
 * Free of React and UI imports so they can be unit-tested in isolation.
 *
 * Ported from the FSE app (Beacon-MMG-App), where this pattern was introduced for
 * the job asset Room filter in MMG-4223. There is no shared package between the
 * two repos, so - as with CommonModal, Banner and FormUtil - the code is copied
 * and allowed to diverge. The FSE copy additionally carries a room-specific
 * predicate that has no meaning here and was left behind.
 */

export type IsimpleOption = Pick<Ioption, 'value' | 'label'>;

/**
 * Case-insensitive label filter for the searchable option list.
 */
export const filterOptionsBySearch = <T extends IsimpleOption>(
    options: T[],
    search: string
): T[] => {
    const term = search.trim().toLowerCase();
    if (!term) {
        return options;
    }
    return options.filter(option => option.label.toLowerCase().includes(term));
};

/**
 * Toggle a single option's membership in the current draft selection.
 */
export const toggleOption = (
    draft: IsimpleOption[],
    option: IsimpleOption
): IsimpleOption[] => {
    const exists = draft.some(item => item.value === option.value);
    if (exists) {
        return draft.filter(item => item.value !== option.value);
    }
    return [...draft, { value: option.value, label: option.label }];
};

/**
 * True when every currently-visible option is already in the draft (and there is
 * at least one visible option). Drives the "Select All" checkbox checked state.
 */
export const areAllVisibleSelected = (
    draft: IsimpleOption[],
    visibleOptions: IsimpleOption[]
): boolean => {
    if (!visibleOptions.length) {
        return false;
    }
    return visibleOptions.every(option =>
        draft.some(item => item.value === option.value)
    );
};

/**
 * Select-all / clear-all scoped to the currently-visible (search-filtered) options.
 * If all visible are already selected they are removed, otherwise all visible are
 * added. Selections outside the visible set are preserved either way, so searching
 * and hitting Select All never silently discards what the user already picked.
 */
export const toggleSelectAllVisible = (
    draft: IsimpleOption[],
    visibleOptions: IsimpleOption[]
): IsimpleOption[] => {
    if (!visibleOptions.length) {
        return draft;
    }
    if (areAllVisibleSelected(draft, visibleOptions)) {
        const visibleValues = new Set(visibleOptions.map(o => o.value));
        return draft.filter(item => !visibleValues.has(item.value));
    }
    const draftValues = new Set(draft.map(item => item.value));
    const additions = visibleOptions
        .filter(option => !draftValues.has(option.value))
        .map(option => ({ value: option.value, label: option.label }));
    return [...draft, ...additions];
};
